import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, Send, ShieldAlert } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Denver" }) + " MT" : "—");

function Settings({ s, save }) {
  const num = (k, label, max) => (
    <label className="text-[11px] text-white/60 flex items-center gap-1">{label}
      <input type="number" min="0" max={max} defaultValue={s[k]} onBlur={(e) => Number(e.target.value) !== s[k] && save({ [k]: Number(e.target.value) })}
        className="input-lifted w-16 text-xs" data-testid={`pipe-${k}`} />
    </label>);
  return (
    <div className="flex flex-wrap gap-3 items-center" data-testid="pipeline-settings">
      <label className="text-xs text-white/80 flex items-center gap-1">
        <input type="checkbox" checked={!!s.auto_publish} onChange={(e) => save({ auto_publish: e.target.checked })} data-testid="pipe-auto-publish" />
        Auto-publish verified articles
      </label>
      {num("run_hour", "Draft hour (MT)", 23)}{num("publish_hour", "Publish hour (MT)", 23)}
      {num("max_drafts_per_run", "Max drafts/run", 10)}{num("max_publish_per_day", "Max published/day", 10)}
    </div>
  );
}

function Held({ rows, act }) {
  if (!rows.length) return <div className="text-xs text-white/40">No held articles.</div>;
  return rows.map((a) => (
    <div key={a.id} className="border border-amber-400/20 rounded p-2 text-[11px]" data-testid={`held-${a.id}`}>
      <div className="text-white">{a.headline} <span className="text-white/40">({a.status})</span></div>
      {(a.hold_reasons || []).map((r) => <div key={r} className="text-amber-300">• {r.slice(0, 220)}</div>)}
      <div className="flex gap-2 mt-1">
        <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="text-lifted-green underline">source</a>
        {a.image && <button onClick={() => act(() => api.post(`/admin/news/articles/${a.id}/release`), "Published.")} className="text-lifted-green underline" data-testid="held-release">Publish anyway (reviewed)</button>}
      </div>
    </div>));
}

function Social({ dests, posts, act }) {
  return (
    <div className="space-y-2" data-testid="social-panel">
      {dests.map((d) => (
        <div key={d.platform} className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={`social-dest-${d.platform}`}>
          <span className="text-white capitalize w-20">{d.platform}</span>
          <span className={d.connected ? "text-lifted-green" : "text-amber-300"}>{d.connected ? "Connected" : "Connection required"}</span>
          <span className="text-white/40">{d.api}{d.connected ? "" : ` — needs ${d.needs}`}</span>
          <label className="ml-auto flex items-center gap-1 text-white/70"><input type="checkbox" checked={!d.paused} onChange={(e) => act(() => api.patch(`/admin/news/social/${d.platform}`, { paused: !e.target.checked }), "Saved.")} data-testid={`social-active-${d.platform}`} />active</label>
        </div>))}
      <div className="max-h-56 overflow-y-auto space-y-1" data-testid="social-queue">
        {posts.map((p) => (
          <div key={p.id} className="text-[11px] border border-white/10 rounded p-2">
            <div><span className="capitalize text-white">{p.platform}</span> · <span className="text-white/70">{p.status.replaceAll("_", " ")}</span> · {p.slug}
              {p.post_url && <> · <a href={p.post_url} className="underline text-lifted-green" target="_blank" rel="noopener noreferrer">post</a></>}
              {["failed", "needs_review"].includes(p.status) && <button onClick={() => act(() => api.post(`/admin/news/social/posts/${p.id}/retry`), "Queued.")} className="ml-2 underline text-lifted-green">retry</button>}
            </div>
            {p.error && <div className="text-rose-300">{p.error}</div>}
            <details><summary className="cursor-pointer text-white/50">Share package</summary>
              <pre className="whitespace-pre-wrap text-white/70">{p.caption}</pre>
              {p.image_url && <a href={p.image_url} target="_blank" rel="noopener noreferrer" className="underline">image</a>}
            </details>
          </div>))}
      </div>
    </div>
  );
}

export function NewsPipelinePanel() {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get("/admin/news/pipeline").then(({ data }) => setD(data)).catch((e) => setMsg(apiErrorMessage(e, "Load failed"))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => { setMsg(""); try { await fn(); setMsg(ok); load(); } catch (e) { setMsg(apiErrorMessage(e, "Action failed")); } };
  if (!d) return null;
  return (
    <section className="glass-panel p-4 mb-6 space-y-4" data-testid="news-pipeline-panel">
      <div className="flex flex-wrap items-center gap-3">
        <CalendarClock className="h-5 w-5 text-lifted-green" />
        <h2 className="font-bebas uppercase tracking-[0.18em] text-white">Daily Pipeline</h2>
        <span className="text-[11px] text-white/60" data-testid="pipe-status">
          Next draft run {fmt(d.next_run)} · Last success {fmt(d.last_success)} · Published today {d.published_today} · Ready {d.ready}
          {" "}· Photos: {d.photo_provider.pexels_configured ? "Pexels connected" : "PEXELS_API_KEY missing"}
        </span>
        <button onClick={() => act(() => api.post("/admin/news/pipeline/publish-ready"), "Ready articles published.")} className="btn-ghost text-xs ml-auto inline-flex gap-1" data-testid="pipe-publish-ready"><Send className="h-3 w-3" /> Publish ready now</button>
      </div>
      <Settings s={d.settings} save={(p) => act(() => api.patch("/admin/news/pipeline/settings", p), "Settings saved.")} />
      {msg && <div className="text-xs text-lifted-green" data-testid="pipe-message">{msg}</div>}
      {d.failures.length > 0 && <div className="text-[11px] text-rose-300">Recent failures: {d.failures.map((f) => `${fmt(f.started_at)} ${(f.errors || []).join("; ")}`).join(" | ")}</div>}
      <div className="grid lg:grid-cols-2 gap-4">
        <div><div className="eyebrow text-white/50 mb-1 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Held for review</div>
          <div className="space-y-1 max-h-72 overflow-y-auto"><Held rows={d.held} act={act} /></div></div>
        <div><div className="eyebrow text-white/50 mb-1">Brand social distribution</div><Social dests={d.social} posts={d.social_posts} act={act} /></div>
      </div>
      <div>
        <div className="eyebrow text-white/50 mb-1">Recently published (pipeline)</div>
        {d.recent_published.map((a) => (
          <div key={a.id} className="text-[11px] text-white/70"><a href={`/lifted-views-news/${a.slug}`} className="underline">{a.headline}</a> · {fmt(a.published_at)} · {a.auto_published ? "auto" : "admin"} · {a.image?.credit || "no image"}</div>))}
      </div>
    </section>
  );
}
